import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { IBidRequirement, IMaterialsResponse } from "@miplatform/types";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import { QuantityField } from "@mibuildweb/components";
import { Box, Typography, useTheme } from "@mui/material";
import { ThinMaterialCard } from "../../../../components/MaterialCard/ThinMaterialCard";

export function RequirementRow({
  requirement,
  actionIcon,
  actionColor,
  materials,
  onAction,
}: Readonly<RequirementRowProps>) {
  const [expanded, setExpanded] = useState(false);
  const { classes } = useStyles();
  const theme = useTheme();

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAction(requirement.id);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.row} onClick={() => setExpanded((prev) => !prev)}>
        <Box className={classes.textContainer}>
          <MiIcon
            path={expanded ? mdiChevronUp : mdiChevronDown}
            color={theme.palette.primary.main}
            size={0.75}
          />
          <Typography variant="BD2" className={classes.name}>
            {requirement.name}
          </Typography>
        </Box>
        <MiIcon
          path={actionIcon}
          color={actionColor}
          onClick={handleActionClick}
          size={1}
        />
      </Box>
      {expanded && (
        <Box className={classes.itemsContainer}>
          {requirement.materials.length === 0 ? (
            <Typography variant="BD2" className={classes.emptyItemsText}>
              There are no Items on this Requirement.
            </Typography>
          ) : (
            requirement.materials.map((material) => {
              const item = materials.find((m) => m.id === material.materialId);

              return (
                <ThinMaterialCard
                  key={material.materialId}
                  item={item}
                  leftAction={
                    <QuantityField quantity={material.quantity} disabled />
                  }
                />
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    overflow: "hidden",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0, 2),
    minHeight: 40,
    cursor: "pointer",
    gap: theme.spacing(1),
  },
  textContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  name: {
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
  },
  itemsContainer: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1, 0),
  },
  emptyItemsText: {
    padding: theme.spacing(1, 2),
    color: theme.palette.text.secondary,
  },
}));

interface RequirementRowProps {
  requirement: IBidRequirement;
  onAction: (requirementId: string) => void;
  actionIcon: string;
  actionColor?: string;
  materials: IMaterialsResponse[];
}
